/**
 * Rebirth, pure (docs/fossil-eras-plan.md). An era ends with an extinction:
 * the herd is buried and what it laid becomes fossils, which raise production
 * in every era after it.
 *
 * `extinct` is the one place the whole state is rebuilt, so it is written as
 * an explicit list of what survives rather than a list of what to clear —
 * a new field added to GameState then resets by default, which is the safe
 * way round.
 */

import {
  ERA_CHOICES,
  ERA_MODIFIERS,
  ERA_MODIFIERS_BY_ID,
  EXPEDITIONS_BY_ID,
  extinctionThreshold,
  FOSSIL_BONUS,
  fossilScale,
} from '../data/eras';
import type { EraModifier, Expedition } from '../data/eras';
import {
  FALLING_STARS_SECONDS,
  HATCHERY_DINOS,
  LONG_MEMORY_DINOS,
  OLD_BONES_EGGS_PER_FOSSIL,
  PROSPECTOR_SHARDS_PER_FOSSIL,
} from '../data/museum';
import type { PerkDef } from '../data/museum';
import { DINOS } from '../data/dinos';
import type { DinoDef } from '../data/dinos';
import { spriteIdFor } from './inventory';
import { createInitialState } from './state';
import type { EraRecord, GameState } from './state';

export function hasPerk(state: GameState, id: string): boolean {
  return state.museum.includes(id);
}

/** On sale: not already owned, and its prerequisite met. */
export function isPerkAvailable(def: PerkDef, state: GameState): boolean {
  if (hasPerk(state, def.id)) return false;
  return def.requires === undefined || hasPerk(state, def.requires);
}

/**
 * Buys a perk with fossils. Spending them lowers the production bonus too,
 * which is the whole tension: a perk is worth more than the 5% it costs you,
 * or it is not worth buying.
 */
export function buyPerk(def: PerkDef, state: GameState): boolean {
  if (!isPerkAvailable(def, state)) return false;
  if (state.fossils < def.cost) return false;
  state.fossils -= def.cost;
  state.museum.push(def.id);
  return true;
}

/**
 * What the Museum changes about the era starting now. Read from the state
 * being left behind, written into the one beginning, so every perk lives in
 * one place instead of being scattered through the systems it touches.
 */
function applyPerks(before: GameState, next: GameState): void {
  // Prospector: an era's unspent shards are not quite wasted.
  if (hasPerk(next, 'museum:prospector')) {
    const extra = Math.floor(before.shards / PROSPECTOR_SHARDS_PER_FOSSIL);
    next.fossils += extra;
    next.fossilsEarned += extra;
  }
  // Hatchery: the first three tiers are already out (the Oviraptor added
  // 2026-09-28, so the perk opens an era at 9 eggs a second rather than 1).
  if (hasPerk(next, 'museum:hatchery')) {
    for (const id of HATCHERY_DINOS) next.dinos[id] = 1;
  }
  // Old Bones: a purse to open with, scaled to what is held now — so it is
  // worth more the more fossils you have kept rather than spent.
  if (hasPerk(next, 'museum:old-bones')) {
    next.eggs = next.fossils * OLD_BONES_EGGS_PER_FOSSIL;
  }
  // Toolbox: the whole pick chain, so the era opens at the drill's sky.
  if (hasPerk(next, 'museum:toolbox')) {
    next.shop.push('tool:stone-hammer', 'tool:iron-pick', 'tool:steel-pick', 'tool:diamond-drill');
  }
  // Warm Blood: what the player bought for their hands, not their herd.
  if (hasPerk(next, 'museum:warm-blood')) {
    next.upgrades.push(...before.upgrades.filter((id) => id.startsWith('click:')));
  }
  // Long Memory: the doublers on the tiers they started with.
  if (hasPerk(next, 'museum:long-memory')) {
    const early = new Set(LONG_MEMORY_DINOS);
    next.upgrades.push(
      ...before.upgrades.filter((id) => {
        const parts = id.split(':');
        return parts[0] === 'dino' && parts[1] !== undefined && early.has(parts[1]);
      }),
    );
  }
}

/**
 * How much faster things fall right now. Falling Stars halves both spawn
 * gaps for the first hour of an era, which is exactly when a fresh herd has
 * nothing else going for it.
 */
export function skyHaste(state: GameState, now: number = Date.now()): number {
  if (!hasPerk(state, 'museum:falling-stars')) return 1;
  const elapsed = (now - state.eraStartedAt) / 1000;
  return elapsed >= 0 && elapsed < FALLING_STARS_SECONDS ? 2 : 1;
}

/** The expedition this era is being run as, or null. */
export function expedition(state: GameState): Expedition | null {
  return state.expedition === null ? null : (EXPEDITIONS_BY_ID.get(state.expedition) ?? null);
}

/**
 * What the era's fossils are multiplied by. A Sprint only pays its triple if
 * the era actually reached the eggs it asked for; falling short is worth the
 * ordinary amount rather than nothing.
 */
export function expeditionMultiplier(state: GameState): number {
  const trip = expedition(state);
  if (!trip) return 1;
  if (trip.requiresEggs !== undefined && state.eggsThisEra < trip.requiresEggs) return 1;
  return trip.fossils;
}

/** Whether the era has run past the length its expedition allows. */
export function expeditionOver(state: GameState, now: number): boolean {
  const trip = expedition(state);
  if (!trip?.endsAfterSeconds) return false;
  return (now - state.eraStartedAt) / 1000 >= trip.endsAfterSeconds;
}

/** The modifier this era is played under, or null for a plain one. */
export function eraModifier(state: GameState): EraModifier | null {
  return state.eraId === null ? null : (ERA_MODIFIERS_BY_ID.get(state.eraId) ?? null);
}

/** What one dinosaur's production is multiplied by under the era's modifier. */
export function eraTierMultiplier(state: GameState, def: DinoDef): number {
  const era = eraModifier(state);
  if (!era) return 1;
  let mult = 1;
  if (era.favours?.dinos.includes(def.id)) {
    mult *= era.favours.mult;
  } else if (era.others !== undefined) {
    mult *= era.others;
  }
  if (def.flies === true && era.flyers !== undefined) mult *= era.flyers;
  return mult;
}

/** Cost multiplier for one dinosaur under the era's modifier. */
export function eraCostMultiplier(state: GameState, def: DinoDef): number {
  const era = eraModifier(state);
  if (!era?.cost) return 1;
  return era.cost.dinos.includes(def.id) ? era.cost.mult : 1;
}

/**
 * Three modifiers to choose between, drawn without repeats from a roll in
 * [0, 1). Deterministic given the rolls, so the choice can be tested and so
 * the same offer survives a reload.
 */
export function offerEras(
  random: () => number = Math.random,
  /** The era being played now, which is never offered again straight away. */
  current: string | null = null,
): EraModifier[] {
  const pool = ERA_MODIFIERS.filter((m) => m.id !== current);
  const out: EraModifier[] = [];
  for (let i = 0; i < ERA_CHOICES && pool.length > 0; i++) {
    const pick = Math.floor(Math.max(0, Math.min(0.999999, random())) * pool.length);
    out.push(...pool.splice(pick, 1));
  }
  return out;
}

/**
 * The eras on offer for the next one. Drawn once per era and kept in the
 * state, so neither reopening the confirm nor reloading the page rerolls
 * them — three to pick from is the limit only if it is the same three. The
 * era being played is never among them: choosing it again would be no choice.
 * An offer naming a modifier that no longer exists, or the current one (saved
 * before that rule), is drawn again.
 */
export function eraOffer(state: GameState, random: () => number = Math.random): EraModifier[] {
  const kept = state.eraOffer
    .map((id) => ERA_MODIFIERS_BY_ID.get(id))
    .filter((m): m is EraModifier => m !== undefined);
  const valid = kept.length > 0 && kept.length === state.eraOffer.length;
  if (valid && kept.every((m) => m.id !== state.eraId)) return kept;
  const drawn = offerEras(random, state.eraId);
  state.eraOffer = drawn.map((m) => m.id);
  return drawn;
}

/** Fossils an era's eggs are worth. Not rounded up: the floor is the reward. */
export function fossilsFor(eggsThisEra: number, era: number): number {
  return Math.floor(Math.cbrt(Math.max(0, eggsThisEra) / fossilScale(era)));
}

/** Eggs still to lay before the Extinction unlocks; 0 once it has. */
export function eggsToExtinction(state: GameState): number {
  return Math.max(0, extinctionThreshold(state.era) - state.eggsThisEra);
}

export function canExtinct(state: GameState): boolean {
  return state.eggsThisEra >= extinctionThreshold(state.era);
}

/** What calling the Extinction right now would pay. */
export function fossilsOnExtinction(state: GameState): number {
  return Math.floor(fossilsFor(state.eggsThisEra, state.era) * expeditionMultiplier(state));
}

/**
 * Production multiplier from the fossils in hand. Spent fossils are gone from
 * `state.fossils`, so buying a Museum perk really does cost the bonus too.
 */
export function fossilMultiplier(state: GameState): number {
  return 1 + Math.max(0, state.fossils) * FOSSIL_BONUS;
}

/**
 * Ends the era. Returns the state the next one starts from, and how many
 * fossils it paid. The caller decides whether to ask first; this does not.
 *
 * Kept: fossils (plus the new ones), Museum perks, achievements and the
 * lifetime counters they were earned against. Everything else is the era —
 * eggs, the herd, upgrades, boosts, names, and the whole meteor economy
 * (shards, shop, splices, evolutions) — and goes.
 */
export function extinct(
  state: GameState,
  now: number = Date.now(),
  /**
   * The dinosaur whose fossil is dug out of the ash. Nothing living crosses
   * the era — a skeleton is what an extinction leaves — so this buys a mount
   * in the Museum, a plaque in its history, and the name it wore is retired
   * for good (see game/names.ts).
   *
   * It used to bring its level and its doublers through as "amber", which
   * made the pick a calculation rather than a memory: tiers are about six
   * times apart, so the answer was always "the newest one you own", and past
   * the middle of the roster it handed the next era its whole threshold in
   * minutes.
   */
  excavated: readonly string[] = [],
  /** The modifier the next era is played under, or null for a plain one. */
  eraId: string | null = null,
  /** The expedition the next era is run as, or null for none. */
  expeditionId: string | null = null,
): { state: GameState; fossils: number } {
  const gained = fossilsOnExtinction(state);
  const next = createInitialState(now);

  next.fossils = state.fossils + gained;
  next.fossilsEarned = state.fossilsEarned + gained;
  next.era = state.era + 1;
  next.museum = [...state.museum];
  next.achievements = [...state.achievements];

  // Lifetime counters: achievements persist, so what earned them must too,
  // or the Stats page would disagree with the shelf.
  next.totalEggs = state.totalEggs;
  next.clicks = state.clicks;
  next.cracks = state.cracks;
  next.shardsEarned = state.shardsEarned;
  next.meteorsBroken = state.meteorsBroken;
  next.meteorKindsBroken = { ...state.meteorKindsBroken };
  next.shared = state.shared;
  next.bonusesCaught = { ...state.bonusesCaught };
  next.feats = [...state.feats];
  // What this era hatched, named and evolved joins the lifetime records
  // (the "every dinosaur, ever" achievements).
  const union = (a: readonly string[], b: readonly string[]): string[] => [
    ...new Set([...a, ...b]),
  ];
  next.everHatched = union(
    state.everHatched,
    Object.keys(state.dinos).filter((id) => (state.dinos[id] ?? 0) > 0),
  );
  next.everNamed = union(state.everNamed, Object.keys(state.names));
  next.everEvolved = union(state.everEvolved, state.evolved);
  // Titles and rests go with the herd; the trophies are kept.
  next.bossTrophies = [...state.bossTrophies];

  // Names go with the herd; the excavated dinosaur keeps its name on its
  // plaque (the era log records it). The name book is no longer added to:
  // it stays only so plaques written before names were recorded there can
  // still show one (ui/history.ts).
  next.nameBook = { ...state.nameBook };
  next.eraId = eraId;
  next.eraStartedAt = now;
  next.expedition = expeditionId;

  // Read the excavated dinosaur while the era it belonged to is still here; after
  // this the herd is gone and neither its name nor its form can be recovered.
  const excavatedId = excavated[0] ?? null;
  const excavatedDef = excavatedId === null ? undefined : DINOS.find((d) => d.id === excavatedId);

  const record: EraRecord = {
    era: state.era + 1,
    eraId: state.eraId,
    expedition: state.expedition,
    seconds: Math.max(0, (now - state.eraStartedAt) / 1000),
    eggs: state.eggsThisEra,
    fossils: gained,
    excavated: excavated[0] ?? null,
    excavatedName: excavatedId === null ? null : (state.names[excavatedId] ?? null),
    excavatedSprite: excavatedDef === undefined ? null : spriteIdFor(excavatedDef, state),
    tiers: Object.values(state.dinos).filter((n) => n > 0).length,
  };
  next.eraLog = [...state.eraLog, record];

  applyPerks(state, next);
  // The era's own head start (the Cretaceous's Pteranodon), and the
  // expedition's (Hands Off's Compsognathus). Never lowers a level a perk
  // already gave.
  for (const id of [
    ...(eraModifier(next)?.freeHatch ?? []),
    ...(expedition(next)?.freeHatch ?? []),
  ]) {
    next.dinos[id] = Math.max(next.dinos[id] ?? 0, 1);
  }
  return { state: next, fossils: gained };
}
